import {
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useState } from 'react';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useIsBottomFromTechnicianCoordinator } from 'shared/permissions/IsBottomFromTechnicianCoordinator';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/it';
import toast from 'shared/utils/toast';
import { useTranslation } from 'react-i18next';
import { maintenanceService } from 'shared/services';
dayjs.locale('it');
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const normalizeDate = (val: any): Dayjs | null => {
  if (!val) return null;

  // 1. If it's already a Dayjs object, check validity
  if (dayjs.isDayjs(val)) {
    return val.isValid() ? val : null;
  }

  // 2. If it's a string, try parsing formats
  if (typeof val === 'string') {
    // Try ISO first (default)
    let parsed = dayjs(val);
    if (parsed.isValid()) return parsed;

    // Try Italian format (Requires customParseFormat plugin)
    parsed = dayjs(val, 'DD/MM/YYYY', true); // 'true' for strict parsing
    if (parsed.isValid()) return parsed;
  }

  // 3. Fallback for native Date objects
  const finalParsed = dayjs(val);
  return finalParsed.isValid() ? finalParsed : null;
};

interface DateSelectorDialogProps {
  dateField: string;
  entityId: number;
  initialDate?: Dayjs | null;
  initialMonth?: Dayjs | null;
}

export const DateSelectorDialog = ({
  dateField,
  entityId,
  initialDate,
  initialMonth
}: DateSelectorDialogProps) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(
    initialDate ? dayjs(initialDate, 'DD/MM/YYYY') : null
  );

  const isDisabledEdit = !useIsBottomFromTechnicianCoordinator();

  const handleClickOpen = () => !isDisabledEdit && setOpen(true);
  const handleClose = () => {
    setSelectedDate(initialDate ? dayjs(initialDate) : null);
    setOpen(false);
  };

  const handleSubmit = () => {
    if (!selectedDate) return;
    const formattedDate = selectedDate.format('YYYY-MM-DD');
    const payload = {
      [dateField]: formattedDate
    };

    maintenanceService.update(entityId, payload).then(() => {
      toast.success(t('toasts.updated-cell'));
      setOpen(false);
    });
  };

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ButtonGroup variant="outlined" size="small">
          {!isDisabledEdit ? (
            <ButtonGroup variant="outlined" size="small">
              <Button onClick={handleClickOpen}>
                {selectedDate ? selectedDate.format('DD/MM/YYYY') : t('actions.insert')}
              </Button>
            </ButtonGroup>
          ) : selectedDate ? (
            <span>{selectedDate.format('DD/MM/YYYY')}</span>
          ) : null}
        </ButtonGroup>

        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>{t('actions.select-date')}</DialogTitle>
          <DialogContent>
            {initialMonth && (
              <DatePicker
                disabled={isDisabledEdit}
                value={normalizeDate(selectedDate)}
                onChange={(newValue) => setSelectedDate(newValue as Dayjs | null)}
                // renderInput={(params) => <TextField {...params} fullWidth />}
                slotProps={{
                  textField: {
                    fullWidth: true
                  }
                }}
              />
            )}
            {!initialMonth && (
              <DatePicker
                disabled={isDisabledEdit}
                value={normalizeDate(selectedDate)}
                onChange={(newValue) => setSelectedDate(newValue as Dayjs | null)}
                // renderInput={(params) => <TextField {...params} fullWidth />}
                slotProps={{
                  textField: {
                    fullWidth: true
                  }
                }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{t('actions.cancel')}</Button>
            <Button onClick={handleSubmit} disabled={!selectedDate}>
              {t('actions.confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </>
  );
};
